import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { SsoCancelledError } from './sso-errors';
import { SsoIdToken, SsoProviderConfig } from './sso-models';

/** Where the popup lands so oidc-client-ts can hand the result to the opener. */
export function getSsoCallbackUrl(): string {
    return `${window.location.origin}/sso-callback`;
}

const managers = new Map<string, UserManager>();

function getUserManager(config: SsoProviderConfig): UserManager {
    if (config.adapter !== 'oidc') {
        throw new Error('SSO_WRONG_ADAPTER');
    }

    const existing = managers.get(config.provider);
    if (existing) {
        return existing;
    }

    const callbackUrl = getSsoCallbackUrl();

    const manager = new UserManager({
        authority: config.authority,
        client_id: config.clientId,
        redirect_uri: callbackUrl,
        popup_redirect_uri: callbackUrl,
        response_type: 'code',
        scope: config.scopes.join(' '),
        extraQueryParams: config.extraQueryParams,
        // Nothing here outlives the sign-in: the ID token goes straight to the
        // API and the session is dropped below. sessionStorage keeps it out of
        // localStorage, where the app's own tokens live.
        stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
        userStore: new WebStorageStateStore({ store: window.sessionStorage }),
        monitorSession: false,
        automaticSilentRenew: false
    });

    managers.set(config.provider, manager);

    return manager;
}

// Neither the library nor the provider reports a dismissal distinctly, so the
// message is most of what there is to match on. `access_denied` is the user
// declining at the provider, which is a dismissal too.
function isCancellation(error: unknown): boolean {
    if (error instanceof Error) {
        if (/closed by user|cancel|access_denied/i.test(error.message)) {
            return true;
        }
    }

    return (
        typeof error === 'object' &&
        error !== null &&
        'error' in error &&
        (error as { error?: unknown }).error === 'access_denied'
    );
}

/**
 * Runs the provider's OIDC flow in a popup and returns the ID token for the API
 * to verify. Every conformant provider goes through this one path — a new one is
 * a config entry, not new code.
 *
 * oidc-client-ts does not validate the `iss` claim, which is what lets a
 * multi-tenant authority work here at all: the discovery document advertises a
 * templated `.../{tenantid}/v2.0` issuer that never matches the real per-tenant
 * `iss`. Nothing is given up, because the client never trusts this token — the
 * API verifies its signature and that `iss` is bound to the token's own `tid`.
 * For the same reason the returned profile must not be used for any decision
 * here; only the token is taken.
 */
export async function signInWithOidc(
    config: SsoProviderConfig
): Promise<SsoIdToken> {
    const manager = getUserManager(config);

    let user: User;
    try {
        user = await manager.signinPopup();
    } catch (error) {
        if (isCancellation(error)) {
            throw new SsoCancelledError();
        }

        throw error;
    }

    // We want the token, not a session.
    void manager.removeUser();

    if (!user.id_token) {
        throw new Error('SSO_NO_ID_TOKEN');
    }

    return { idToken: user.id_token, nonce: user.profile.nonce };
}

/** Completes the popup handshake. Called only by the callback route. */
export async function handleSsoPopupCallback(): Promise<void> {
    // The settings are irrelevant on this side: the callback only forwards the
    // resulting URL to the window that opened it.
    const manager = new UserManager({
        authority: '',
        client_id: '',
        redirect_uri: getSsoCallbackUrl()
    });

    await manager.signinPopupCallback();
}
